import { Button, Form, Image, message } from 'antd';
import Title from 'antd/lib/typography/Title';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import BookyBox from '../../Components/Layout/BookyBox';
import { BookyInput } from '../../Components/Forms/BookyInput';
import PagePublicSite from '../../Components/Page/PagePublicSite';
import NotFound from '../../Components/Page/NotFoundPage';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';
import { WebApiCompanyDtoCompanyPublicSiteDto } from '../../Repository/eventbookyapi';
import { PublicSiteService } from '../../Services/PublicSiteService';
import { TextService } from '../../Services/TextService';
import { PublicResultType } from './Types/PublicResultType';

enum PublicSiteLoginState {
    Undefined,
    CompanyNotFound,
    CompanyFound,
    MemberCodeNotFoundOrExpired
}

const PublicSiteLogin = () => {
    const params = useParams();
    const navigation = useNavigate();
    const location = useLocation();
    const [form] = Form.useForm();
    const GetText = TextService.GetText;

    const [state, setState] = useState(PublicSiteLoginState.Undefined as PublicSiteLoginState);
    const [publicSite, setPublicSite] = useState([] as WebApiCompanyDtoCompanyPublicSiteDto);

    const language = getCurrentLanguage();
    const companyUrl = params.companyurl;

    const getEditMemberPath = () => `/${language}/${companyUrl}/editmember`;

    useEffect(() => {
        const load = async () => {
            if (!companyUrl) {
                setState(PublicSiteLoginState.CompanyNotFound);
                return;
            }

            if (params.passwordcode) {
                await loginWithPasswordCode(params.passwordcode);
                return;
            }

            try {
                const company = await PublicSiteService.GetCompanyPublicSiteByUrl(companyUrl);
                setPublicSite(company);
                setState(PublicSiteLoginState.CompanyFound);

                try {
                    // Works for both regular and family members; the /editmember landing then
                    // routes to the right self-edit page.
                    await PublicSiteService.GetSession();
                    navigation(getEditMemberPath(), { replace: true });
                } catch {
                }
            } catch {
                setState(PublicSiteLoginState.CompanyNotFound);
            }
        };

        load();
    }, [companyUrl, params.passwordcode]);

    const loginWithPasswordCode = async (passwordCode: string) => {
        try {
            await PublicSiteService.LoginWithPasswordCode({
                companyUrl,
                passwordCode
            });

            const from = (location.state as any)?.from;
            if (from) {
                navigation(from, { replace: true });
                return;
            }

            navigation(getEditMemberPath(), { replace: true });
        } catch {
            setState(PublicSiteLoginState.MemberCodeNotFoundOrExpired);
        }
    };

    const getDownloadUrl = (dto: WebApiCompanyDtoCompanyPublicSiteDto) => {
        return process.env.REACT_APP_API_URL + '/Image/downloadImage?bloburl=' + dto.siteLogoPath;
    };

    const onLoginRequest = async (formValues: any) => {
        try {
            await PublicSiteService.CreatePasswordCode({
                companyUrl,
                email: formValues.email
            });
        } catch {
            message.warning(GetText('PublicSiteEditMember_Error'));
            return;
        }

        navigation(`/${language}/${companyUrl}/success/${PublicResultType.MailHasBeenSent}`);
    };

    if (state === PublicSiteLoginState.CompanyNotFound) {
        return <NotFound message={GetText('PublicSiteEditMember_CompanyNotFound')} />;
    }

    if (state === PublicSiteLoginState.MemberCodeNotFoundOrExpired) {
        return (
            <PagePublicSite>
                <BookyBox>
                    <NotFound message={GetText('PublicSiteEditMember_MemberNotFoundOrExpired')} />
                    <div style={{ textAlign: 'center' }}>
                        <a
                            onClick={() => {
                                setState(PublicSiteLoginState.CompanyFound);
                            }}
                        >
                            Till inloggningen
                        </a>
                    </div>
                </BookyBox>
            </PagePublicSite>
        );
    }

    return (
        <PagePublicSite>
            <BookyBox>
                {publicSite.siteLogoPath ? (
                    <div style={{ marginBottom: '20px' }}>
                        <Image preview={false} className='company-logo' src={getDownloadUrl(publicSite)} />
                    </div>
                ) : (
                    <Title style={{ margin: 0 }}>{publicSite.siteTitle}</Title>
                )}

                <h2>{GetText('PublicSiteEditMember_LoginMemberTitle')}</h2>

                <Form
                    data-testid='create-form'
                    name='basic'
                    form={form}
                    colon={false}
                    labelCol={{
                        span: 7,
                    }}
                    onFinish={onLoginRequest}
                    wrapperCol={{
                        span: 15,
                    }}
                    layout='horizontal'
                    initialValues={{
                        remember: true,
                    }}
                    autoComplete='off'
                >
                    <BookyInput
                        label={GetText('PublicSiteEditMember_Email')}
                        placeholder={GetText('PublicSiteEditMember_PlaceHolder_Email')}
                        validateEmailIfFilledIn={true}
                        name='email'
                        required={true}
                        requiredMessage={GetText('PublicSiteEditMember_PlaceHolder_Email')}
                    />

                    <Form.Item
                        label=' '
                        name='submit'
                        rules={[
                            {
                                required: false,
                                message: 'submitbutton',
                            },
                        ]}
                    >
                        <Button data-testid='submit-button' style={{ float: 'left' }} size='middle' type='primary' htmlType='submit'>
                            {GetText('PublicSiteEditMember_SendEmail')}
                        </Button>
                    </Form.Item>
                </Form>
            </BookyBox>
        </PagePublicSite>
    );
};

export default PublicSiteLogin;
